import {
  CaretDownOutlined,
  CaretUpOutlined,
  CheckCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Collapse,
  Divider,
  Input,
  Modal,
  Radio,
  Select,
  Steps,
} from 'antd';
import {jwtDecode} from 'jwt-decode';
import {useMemo, useRef, useState} from 'react';
import {toast} from 'react-hot-toast';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useLocation, useNavigate} from 'react-router-dom';

import {
  RQCreateRazorpayOrder,
  RQGetPromocodeAPI,
  RQGetUserDetails,
  RQOrderRegistration,
  RQVerifyRazorpayPayment,
  RqGetAllProducts,
  RqUpdateOrder,
} from '../../reactQuery/apis';
import {clearCart} from '../../redux/authAction';
import {selectAuth} from '../../redux/selectors';
import {LoginScreen} from '../LoginScreen';

import {AddressSection} from './AddressSection';
import {OrderSummary} from './OrderSummary';

const CheckOutScreen = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const location = useLocation();

  const directProduct = location.state?.product || null;

  const [isGstModalOpen, setIsGstModalOpen] = useState(false);

  const [gstIn, setGstIn] = useState('');

  const [tempGstIn, setTempGstIn] = useState('');

  const [businessName, setBusinessName] = useState('');

  const [showLoginModal, setShowLoginModal] = useState(false);

  const orderPayloadRef = useRef(null);

  const auth = useSelector(selectAuth);

  const {loggedIn} = useSelector(state => state.auth);

  const token = auth?.tokenData;

  const decodedToken = token ? jwtDecode(token) : null;

  const userId = decodedToken?.userId;

  const id = decodedToken?.userId;

  const cartItems = useSelector(state => state.cart.items) || [];

  const {data: allProducts} = RqGetAllProducts();

  const storedProduct = directProduct ? [directProduct] : cartItems;
  const totalQuantity = storedProduct.reduce(
    (sum, item) => sum + item.quantity,
    0,
  );

  const [selectedPromoCode, setSelectedPromoCode] = useState(null);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [appliedDiscount, setAppliedDiscount] = useState(0);

  const [isAddressComplete, setIsAddressComplete] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState('ONLINE_PAYMENT');

  const openRazorpay = rzpData => {
    const options = {
      key: rzpData.key,
      order_id: rzpData.razorpayOrderId,
      amount: rzpData.amount,
      currency: rzpData.currency,
      name: 'Kalapi Stores',
      description: 'Kalapi Stores Payment',

      handler: response => {
        verifyPayment({
          token,
          formData: {
            userId: id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          },
        });
      },

      prefill: {
        name: `${userData?.firstName || ''} ${userData?.lastName || ''}`,
        email: userData?.email || '',
        contact: userData?.mobile || '',
      },

      theme: {color: '#000'},
    };

    const razorpayObject = new window.Razorpay(options);
    razorpayObject.open();
  };
 
  const {mutate: createRazorpayOrder} = RQCreateRazorpayOrder({
    onSuccess: rzpResponse => {
      openRazorpay(rzpResponse);
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'Something Went Wrong');
    },
  });

  const {mutate: verifyPayment} = RQVerifyRazorpayPayment({
    onSuccess: (response, variables) => {

      const finalPayload = {
        ...orderPayloadRef.current,

        transactionId: variables.formData.razorpay_payment_id,
        transactionStatus: 'FULL_PAYMENT',
        status: 'CONFIRMED',
      };

      mutateAsync(finalPayload);

    },

    onError: () => {
      toast.error('Payment verification failed');
    },
  });

  const enrichedProducts = useMemo(() => {
    if (!allProducts || !Array.isArray(allProducts)) {
      return [];
    }

    return storedProduct
      .map(item => {
        const product = allProducts.find(p => p.id === item.productId);
        if (!product) {
          return null;
        }

        const basePrice = product.price || 0;
        const discountPercent = product.discount || 0;

        const finalPrice =
          discountPercent > 0
            ? basePrice - (basePrice * discountPercent) / 100
            : basePrice;

        const variant = product.variants?.find(v => v.color === item.color);

        let availableStock = 0;

        if (variant) {
          const validSizes = variant.sizes?.filter(
            s => s.size && s.size.trim() !== '',
          );

          if (!validSizes || validSizes.length === 0) {
            availableStock = variant.sizes?.[0]?.quantity || 0;
          } else {
            const sizeObj = validSizes.find(s => s.size === item.size);
            availableStock = sizeObj?.quantity || 0;
          }
        }

        return {
          ...item,
          productName: product.productName,
          imageUrl: product.imageUrl,
          pricePerUnit: finalPrice,
          promocodeInfo: product.promocodeInfo || null,
          availableStock,
        };
      })
      .filter(Boolean);
  }, [storedProduct, allProducts]);

  const subTotal = useMemo(() => {
    return enrichedProducts.reduce(
      (sum, item) => sum + item.pricePerUnit * item.quantity,
      0,
    );
  }, [enrichedProducts]);

  const cartContext = {
    productIds: storedProduct.map(i => i.productId),
    categories: storedProduct.map(i => i.category).filter(Boolean),
    brands: storedProduct.map(i => i.brand).filter(Boolean),
    subTotal,
  };

  const {data: userData} = RQGetUserDetails(userId, token);

  const {mutateAsync} = RQOrderRegistration(token, {
    onSuccess: () => {

      dispatch(clearCart(userId));

      toast.success('Order placed successfully');

      navigate('/userhistory?tab=orders', {
        replace: true,
      });

    },
  });

  const cartProductPromos = useMemo(() => {
    return enrichedProducts
      .map(item => {
        const promo = item.promocodeInfo;

        if (!promo || !promo.code || promo.isActive === false) {
          return null;
        }

        return {
          code: promo.code,
          discountValue: Number(promo.discountValue || 0),
          cartItemKey: `${item.productId}-${promo.code}`,
        };
      })
      .filter(Boolean);
  }, [enrichedProducts]);

  const handlePromoSelect = value => {
    if (!value) {
      setAppliedPromo(null);
      setAppliedDiscount(0);
      setSelectedPromoCode(null);
      return;
    }

    const promo = cartProductPromos.find(p => p.cartItemKey === value);
    if (!promo) {
      return;
    }

    const discount = (subTotal * promo.discountValue) / 100;

    setAppliedPromo(promo);
    setAppliedDiscount(discount);
    setSelectedPromoCode(value);
  };

  const totalAmount = Math.max(subTotal - appliedDiscount, 0);

  const loadRazorpayScript = () => {
    return new Promise(resolve => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };
  const handlePlaceOrder = async () => {
    if (!loggedIn) {
      toast.error('Please login before placing your order.');
      setShowLoginModal(true);
      return;
    }

    if (!isAddressComplete) {
      toast.error(
        'Please complete your address details before placing the order.',
      );
      return;
    }

    for (const item of enrichedProducts) {
      if (item.quantity > item.availableStock) {
        toast.error(
          `Only ${item.availableStock} items available for ${item.productName}`,
        );
        return;
      }
    }
    const productsPayload = storedProduct.map(item => ({
      productId: item.productId,
      promoCode: item.promocodeInfo?.code || null,
      quantity: item.quantity,
      size:
        typeof item.size === 'string' && item.size.trim() !== ''
          ? item.size.trim()
          : '',
      color: item.color || null,
    }));

    const orderItems = storedProduct.map(item => ({
      name: item.productName,
      sku: item.productId,
      units: item.quantity,
      selling_price: item.pricePerUnit,
    }));

    const totalQuantity = enrichedProducts.reduce(
      (sum, item) => sum + item.quantity,
      0,
    );
    const deliveryCharges = 0;
    const finalSubTotal = totalAmount;
    const appliedPromoCode = appliedPromo?.code || '';

    const now = new Date();
    const formattedDate = `${now.getFullYear()}-${String(
      now.getMonth() + 1,
    ).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(
      now.getHours(),
    ).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    // COD
    if (paymentMethod === 'COD') {
      try {
        const payload = {
          userId: id,
          products: productsPayload,

          totalQuantity,
          totalAmount: finalSubTotal,
          subTotal: finalSubTotal,
          deliveryCharges,
          collectableAmount: finalSubTotal,

          appliedPromoCode,
          appliedPromoCodePercentage: appliedPromo?.discountValue || 0,

          gstIn: gstIn || '',
          businessName: businessName || '',

          shippingIsBilling: true,

          status: 'CREATED',
          estimatedDeliveryDate: null,

          orderId: `COD-${Date.now()}`,
          orderDate: formattedDate,
          transactionId: `COD-${Date.now()}`,
          paymentMethod: 'COD',

          pickupLocation: 'work',

          billingCustomerName: userData?.firstName,
          billingLastName: userData?.lastName,
          billingAddress: userData?.address,
          billingCity: userData?.city,
          billingPincode: userData?.pinCode,
          billingState: userData?.state,
          billingCountry: 'India',
          billingEmail: userData?.email,
          billingPhone: userData?.mobile,

          length: 30,
          breadth: 11,
          height: 25,
          weight: 0.5,
          transactionStatus: 'FULL_PAYMENT',
          orderItems,
        };

        await mutateAsync(payload);

        dispatch(clearCart(userId));

        localStorage.removeItem('checkoutProduct');
        navigate('/userhistory?tab=orders', {replace: true});
        return;
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to place COD order.',
        );
        return;
      }
    }
    //online payment flow
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error('Razorpay SDK failed to load.');
        return;
      }

      const orderPayload = {
        userId: id,
        products: productsPayload,
        totalQuantity,
        totalAmount: Math.round(finalSubTotal),
        subTotal: Math.round(finalSubTotal),
        deliveryCharges,
        collectableAmount: 0,
        appliedPromoCode,
        appliedPromoCodePercentage: appliedPromo?.discountValue || 0,
        gstIn: gstIn || '',
        businessName: businessName || '',
        shippingIsBilling: true,
        status: 'CREATED',
        orderId: `PREPAID-${Date.now()}`,
        estimatedDeliveryDate: null,
        orderDate: formattedDate,
        paymentMethod: 'Prepaid',
        pickupLocation: 'work',
        billingCustomerName: userData?.firstName,
        billingLastName: userData?.lastName,
        billingAddress: userData?.address,
        billingCity: userData?.city,
        billingPincode: userData?.pinCode,
        billingState: userData?.state,
        billingCountry: 'India',
        billingEmail: userData?.email,
        billingPhone: userData?.mobile,
        length: 30,
        breadth: 11,
        height: 25,
        weight: 0.5,
        transactionStatus: 'PENDING',
        transactionId: '',
        orderItems,
      };
      orderPayloadRef.current = orderPayload;

      createRazorpayOrder({
        token,
        orderId: orderPayload.orderId, 
        amount: Math.round(finalSubTotal),
      });

    } catch (error) {
      console.error(error);
      toast.error('Failed to initiate payment');
    }
    console.log ('button clicked');
  };
  const steps = [
    {title: 'Login', status: loggedIn ? 'finish' : 'process'},
    {
      title: 'Address',
      status: isAddressComplete ? 'finish' : loggedIn ? 'process' : 'wait',
    },
    {title: 'Order', status: 'wait'},
  ];

  const GSTIN_REGEX =
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

  const isValidGSTIN = !tempGstIn || GSTIN_REGEX.test(tempGstIn.toUpperCase());

  return (
    <div
      className={
        'flex w-full items-center justify-center lg:px-20 p-4 lg:mb-20'
      }
    >
      <div className={'max-w-7xl w-full'}>
        <div
          className={
            'grid grid-cols-1 lg:grid-cols-12 lg:gap-5 w-full max-w-screen-lg mx-auto p-4 lg:p-0'
          }
        >
          <div className={'lg:col-span-12 mt-5 lg:block hidden'}>
            <Steps
              current={steps.findIndex(step => step.status === 'process')}
              items={steps}
            />
          </div>

          <div className={'lg:col-span-7 md:mt-5 mt-12'}>
            <div className={'flex flex-col gap-4'}>
              <Collapse
                className={'w-full shadow-md'}
                bordered={false}
                defaultActiveKey={['1']}
                expandIcon={({isActive}) =>
                  isActive ? <CaretUpOutlined /> : <CaretDownOutlined />
                }
                expandIconPosition={'end'}
              >
                <Collapse.Panel
                  key={'1'}
                  header={(
                    <span
                      className={
                        'flex items-center font-semibold text-primary-900 w-full lg:text-base'
                      }
                    >
                      Login{' '}
                      {loggedIn && <CheckCircleOutlined className={'ml-2'} />}
                    </span>
                  )}
                >
                  <div className={'flex flex-col gap-2'}>
                    {loggedIn ? (
                      <p className={'text-primary-950 flex-col  font-semibold'}>
                        {userData?.firstName} {userData?.lastName}
                        <br />
                        {userData?.email}
                      </p>
                    ) : (
                      <Button
                        onClick={() => setShowLoginModal(true)}
                        className={'!bg-primary-800 w-32 !text-white'}
                      >
                        Login
                      </Button>
                    )}
                  </div>
                </Collapse.Panel>
              </Collapse>
              {loggedIn && (
                <Collapse
                  className={'w-full shadow-md'}
                  bordered={false}
                  defaultActiveKey={['1']}
                  expandIcon={({isActive}) =>
                    isActive ? <CaretUpOutlined /> : <CaretDownOutlined />
                  }
                  expandIconPosition={'end'}
                >
                  <Collapse.Panel
                    header={(
                      <span
                        className={
                          'flex items-center font-semibold text-primary-900 w-full lg:text-base'
                        }
                      >
                        Address{' '}
                        {isAddressComplete && (
                          <CheckCircleOutlined
                            className={'ml-2 text-primary-900'}
                          />
                        )}
                      </span>
                    )}
                    key={'1'}
                  >
                    <AddressSection setAddressComplete={setIsAddressComplete} />
                  </Collapse.Panel>
                </Collapse>
              )}

              {loggedIn && (
                <Collapse
                  className={'w-full shadow-md'}
                  bordered={false}
                  defaultActiveKey={['1']}
                  expandIcon={({isActive}) =>
                    isActive ? <CaretUpOutlined /> : <CaretDownOutlined />
                  }
                  expandIconPosition={'end'}
                >
                  <Collapse.Panel
                    header={(
                      <span
                        className={
                          'flex items-center font-semibold text-primary-900 w-full lg:text-base'
                        }
                      >
                        Order Summary
                      </span>
                    )}
                    key={'2'}
                  >
                    <OrderSummary product={enrichedProducts} />
                  </Collapse.Panel>
                </Collapse>
              )}

              {/* Payment Methods */}
              {loggedIn && (
                <Collapse
                  className={'w-full shadow-md'}
                  bordered={false}
                  defaultActiveKey={['1']}
                  expandIcon={({isActive}) =>
                    isActive ? <CaretUpOutlined /> : <CaretDownOutlined />
                  }
                  expandIconPosition={'end'}
                >
                  <Collapse.Panel
                    header={(
                      <span
                        className={
                          'flex items-center font-semibold text-primary-900 w-full lg:text-base'
                        }
                      >
                        Payment Methods{' '}
                      </span>
                    )}
                    key={'1'}
                  >
                    <Radio.Group
                      className={'flex flex-col gap-4'}
                      value={paymentMethod}
                      onChange={e => setPaymentMethod(e.target.value)}
                    >
                      <Radio value={'ONLINE_PAYMENT'}>
                        Razorpay (Card, UPI, Netbanking)
                      </Radio>
                      <Radio value={'COD'}>Cash On Delivery (COD)</Radio>
                    </Radio.Group>
                  </Collapse.Panel>
                </Collapse>
              )}
            </div>
          </div>

          {/* Right Panel */}
          <div
            className={'lg:col-span-5 lg:ml-4 mt-5 lg:sticky top-0 self-start'}
          >
            <div
              className={'bg-primary-50 rounded-lg p-3 max-w-[800px] w-full'}
            >
              <h1 className={'text-primary-900 font-semibold lg:text-lg'}>
                Price Details
              </h1>

              <Divider className={'my-1'} />

              {/* Subtotal */}
              <div
                className={
                  'flex justify-between md:text-base font-semibold mb-3'
                }
              >
                <p>Price ({totalQuantity} items)</p>
                <p>₹ {Math.round(subTotal)}</p>
              </div>

              {/* Promo Section */}
              <div className={'my-3'}>
                <p className={'font-semibold mb-1'}>Apply Promo Code</p>

                <Select
                  placeholder={'Select a promo code'}
                  value={selectedPromoCode}
                  onChange={handlePromoSelect}
                  allowClear
                  style={{width: '100%'}}
                  options={cartProductPromos.map(promo => ({
                    label: `${promo.code} - ${promo.discountValue}% OFF`,
                    value: promo.cartItemKey,
                  }))}
                />

                {appliedPromo && (
                  <p className={'text-[#32CD32] mt-2 flex justify-between'}>
                    <span>Applied: {appliedPromo.code}</span>
                    <span>- ₹ {Math.round(appliedDiscount)}</span>
                  </p>
                )}

                {!cartProductPromos.length && (
                  <p className={'text-gray-500 text-sm mt-2'}>
                    No promo codes available for this cart
                  </p>
                )}
              </div>
              <div className={'flex items-center gap-2 mt-4 mb-4'}>
                <PlusOutlined
                  className={'cursor-pointer text-sm'}
                  onClick={() => {
                    setTempGstIn(gstIn);
                    setIsGstModalOpen(true);
                  }}
                />
                <button
                  className={'text-sm font-light'}
                  onClick={() => {
                    setTempGstIn(gstIn);
                    setIsGstModalOpen(true);
                  }}
                >
                  Add GST Invoice(optional){' '}
                </button>
              </div>

              <Modal
                title={'Add GST Details'}
                open={isGstModalOpen}
                onCancel={() => setIsGstModalOpen(false)}
                footer={[
                  <Button
                    key={'cancel'}
                    onClick={() => setIsGstModalOpen(false)}
                  >
                    Cancel
                  </Button>,
                  <Button
                    key={'save'}
                    type={'primary'}
                    onClick={() => {
                      setGstIn(tempGstIn.toUpperCase());
                      setIsGstModalOpen(false);
                    }}
                  >
                    Save
                  </Button>,
                ]}
              >
                <Input
                  placeholder={'e.g., 27ABCDE1234F2Z5'}
                  value={tempGstIn}
                  maxLength={15}
                  onChange={e => setTempGstIn(e.target.value.toUpperCase())}
                  status={!isValidGSTIN ? 'error' : ''}
                  className={'mb-3'}
                />

                <Input
                  placeholder={'Business Name'}
                  value={businessName}
                  onChange={e => setBusinessName(e.target.value)}
                />
              </Modal>
              {/* Delivery */}
              <div
                className={
                  'flex justify-between md:text-base font-semibold mb-3'
                }
              >
                <p>Delivery Charges</p>
                <p className={'text-[#32CD32]'}>FREE</p>
              </div>

              <Divider />

              {/* Total */}
              <div
                className={
                  'flex justify-between md:text-xl text-lg font-bold mt-4'
                }
              >
                <p>Total Charges</p>
                <p>₹ {Math.round(totalAmount)}</p>
              </div>
            </div>

            {/* Terms */}
            <p className={'text-sm font-extralight mt-2'}>
              By placing your order, you agree to Kalapi Stores
              <Link
                to={'/privacy'}
                target={'_blank'}
                className={'underline ml-1'}
              >
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link
                to={'/terms'}
                target={'_blank'}
                className={'underline ml-1'}
              >
                Terms and conditions
              </Link>
              .
            </p>

            {/* Place Order */}
            {loggedIn && (
              <Button
                onClick={handlePlaceOrder}
                disabled={!isAddressComplete}
                className={
                  'mt-5 w-full h-10 rounded-lg border-none bg-primary-900 text-white font-semibold'
                }
              >
                Place Your Order
              </Button>
            )}

            {loggedIn && !isAddressComplete && (
              <p className={'text-red-600 text-sm mt-2'}>
                Please complete your address details.
              </p>
            )}
          </div>
        </div>
        {showLoginModal && (
          <LoginScreen onClose={() => setShowLoginModal(false)} />
        )}
      </div>
    </div>
  );
};

export default CheckOutScreen;
